import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Button } from "../ui/button";
import { FaCopy } from "react-icons/fa";
import { regenerateApiKeyForProject } from "../../api/admin/projects";
import { notify } from "../../lib/utils";

const RegenerateApiKeyDialog = ({
  open,
  onClose,
  project,
  onProjectUpdated,
}: any) => {
  console.log("project", project);
  // before allowing to close the dialog, we should force user to copy the new api key,
  // if he regenerates the api key, then closes the dialog without copying the new api key.

  const [isRegenerated, setIsRegenerated] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [newApiKey, setNewApiKey] = React.useState<any>(null);
  const [isCopied, setIsCopied] = React.useState(false);

  const handleOnClose = () => {
    if (isRegenerated && !isCopied) {
      notify({
        type: "error",
        message: "Please copy the new API key before closing the dialog",
      });
      return;
    }

    onClose();
    // reset inner state.
    setIsRegenerated(false);
    setIsCopied(false);
    setNewApiKey(null);
  };

  const handleCopy = () => {
    console.log("copy");
    navigator.clipboard.writeText(newApiKey);
    setIsCopied(true);
  };

  const handleRegenerate = async () => {
    if (!project) return;
    setLoading(true);
    setIsCopied(false);

    const response = await regenerateApiKeyForProject(project?._id);
    if (!response || response.status !== 200) {
      notify({
        type: "error",

        message: "Error regenerating the api key",
      });
      setLoading(false);
      return;
    }

    setIsRegenerated(true);
    setNewApiKey(response.data.apiKey);
    setLoading(false);

    onProjectUpdated(response.data.project);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOnClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-[rgba(0,0,0,.6)] data-[state=open]:animate-overlayShow fixed inset-0" />

        <Dialog.Content className="data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[90vh] w-full max-w-[60vw] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-blackLight p-[25px] shadow-[hsl(206, 22%, 93%) 0px 10px 38px -10px, hsl(206, 22%, 93%) 0px 10px 20px -15px] focus:outline-none text-body">
          <Dialog.Title className="text-mauve12 m-0 text-[17px] font-medium">
            Are you sure you want to regenerate the API key for this project?{" "}
          </Dialog.Title>
          <div className="my-8 flex flex-col">
            <p className="text-red-500">
              Regenerating the API key will invalidate the current key and the
              project will no longer be able to access the API using the current
              key. This action cannot be undone.
            </p>

            {project && (
              <div className="pt-10 flex flex-col gap-y-1">
                <span>
                  Project ID:{" "}
                  <span className="text-primary">{project?._id}</span>
                </span>

                <span>
                  Project Name:{" "}
                  <span className="text-primary">{project?.name}</span>
                </span>

                <span>
                  Project Slug:{" "}
                  <span className="text-primary">{project?.slug}</span>
                </span>

                <span>
                  Last Api Key Generated At:{" "}
                  <span className="text-primary">
                    {new Date(project?.apiKeyGeneratedAt).toLocaleString()}
                  </span>
                </span>

                <span>
                  Last Api Key Generated By:{" "}
                  <span className="text-primary">
                    {project?.apiKeyGeneratedBy}
                  </span>
                </span>
              </div>
            )}

            {isRegenerated && newApiKey && (
              <div className="pt-5">
                <h3 className="text-mauve12 text-[17px] font-medium flex flex-col">
                  New API Key
                  {(isCopied && (
                    <span className="text-green-500"> Copied!</span>
                  )) || (
                    <span className="text-red-500">
                      This is the only time you will see the API key (Click on
                      it to copy)
                    </span>
                  )}
                </h3>

                <div
                  title={"Click to copy"}
                  onClick={handleCopy}
                  className="group flex cursor-copy gap-x-2 relative items-center justify-center mt-2 p-4 bg-blackLight font-bold rounded-md border text-center"
                >
                  <span>{newApiKey}</span>
                </div>
              </div>
            )}
          </div>

          <div className="py-2 h-full w-full flex items-end justify-end ">
            <Button
              disabled={loading}
              loading={loading}
              onClick={handleRegenerate}
              className="bg-red-500 hover:bg-red-600"
            >
              Regenerate API KEY
            </Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="text-white hover:text-primary focus:shadow-violet7 absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
              aria-label="Close"
            >
              <Cross2Icon />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default RegenerateApiKeyDialog;
